﻿import dayjs from 'lib/dayjs';
import Icon from '../../../components/AppIcon';
import { mapLeaveType, LEAVE_TYPES } from '../../../utils/common';

const formatContact = (contactStr) => {
  if (!contactStr) return '';
  try {
    const parsed = JSON.parse(contactStr);
    return `${parsed.name} - ${parsed.number}`;
  } catch {
    return contactStr;
  }
};

const LeaveDetailsModal = ({ isOpen, onClose, leave, onApprove, onReject, workSchedule }) => {
  if (!isOpen || !leave) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: 'CircleCheckBig' },
      APPROVED: { color: 'bg-green-100 text-green-800', icon: 'CircleCheckBig' },
      REJECTED: { color: 'bg-red-100 text-red-800', icon: 'X' },
      CANCELLED: { color: 'bg-red-100 text-red-800', icon: 'X' },
    };

    const config = statusConfig?.[status] || statusConfig?.['PENDING'];

    return (
      <div
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color} gap-1`}
      >
        <Icon name={config?.icon} size={14} />
        {status}
      </div>
    );
  };

  const calculateDuration = (start_date_time, end_date_time) => {
    if (!start_date_time || !end_date_time) return '0 hours';

    const start = new Date(start_date_time);
    const end = new Date(end_date_time);
    const diffMs = end - start;

    if (diffMs < 0) return '0 hours';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return diffMinutes > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffHours} hours`;
    } else {
      return `${diffMinutes} minutes`;
    }
  };

  const calculateWorkHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';

    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;

    if (diffMs < 0) return '0 hours';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return diffMinutes > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffHours} hours`;
    } else {
      return `${diffMinutes} minutes`;
    }
  };

  const calculateDaysDifference = (start_date_time, end_date_time) => {
    const start = new Date(start_date_time);
    const end = new Date(end_date_time);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const getDurationDisplay = (leaveItem) => {
    if (leaveItem?.duration) return leaveItem.duration;

    switch (leaveItem?.type) {
      case LEAVE_TYPES.FULL_DAY:
        return calculateWorkHours(workSchedule?.dayStartTime, workSchedule?.dayEndTime);

      case LEAVE_TYPES.HALF_DAY:
        if (leaveItem?.halfDayType === 'FIRST_HALF') {
          return calculateWorkHours(workSchedule?.dayStartTime, workSchedule?.breakResumeTime);
        } else {
          return calculateWorkHours(workSchedule?.breakResumeTime, workSchedule?.dayEndTime);
        }

      case LEAVE_TYPES.SHORT:
        if (leaveItem?.start_date_time && leaveItem?.end_date_time) {
          return calculateDuration(leaveItem?.start_date_time, leaveItem?.end_date_time);
        }
        return 'N/A';

      case LEAVE_TYPES.LONG_TIME:
        if (leaveItem?.start_date_time && leaveItem?.end_date_time) {
          const days = calculateDaysDifference(
            leaveItem?.start_date_time,
            leaveItem?.end_date_time,
          );
          const dailyHours = calculateWorkHours(
            workSchedule?.dayStartTime,
            workSchedule?.dayEndTime,
          );
          return `${days} day${days > 1 ? 's' : ''} (${dailyHours} per day)`;
        }
        return 'N/A';

      case LEAVE_TYPES.MULTIPLE_DAY:
        if (leaveItem?.dates?.length > 0) {
          const dailyHours = calculateWorkHours(
            workSchedule?.dayStartTime,
            workSchedule?.dayEndTime,
          );
          return `${leaveItem?.dates?.length} day${leaveItem?.dates?.length > 1 ? 's' : ''} (${dailyHours} per day)`;
        }
        return 'N/A';

      default:
        return 'N/A';
    }
  };

  const isPending = leave.status === 'PENDING';
  const isMultipleDay =
    (leave.type === LEAVE_TYPES.MULTIPLE_DAY && leave.dates?.length > 0) ||
    (leave.type === LEAVE_TYPES.LONG_TIME && leave.dates?.length > 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Leave Request Details</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
            {/* Employee Info */}
            <div className="flex items-center gap-4 mb-8 bg-secondary-50 p-4 rounded-xl">
              <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold text-lg">
                {leave.user?.first_name?.[0]}
                {leave.user?.last_name?.[0]}
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">
                  {leave.user?.first_name} {leave.user?.last_name}
                </h4>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Icon name="IdCard" size={14} />
                    {leave.user?.employee_id}
                  </span>
                  <span>•</span>
                  <span>{leave.user?.designation || 'Employee'}</span>
                </div>
              </div>
              <div className="ml-auto">{getStatusBadge(leave.status)}</div>
            </div>
            {/* Leave Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    <Icon name="FileText" size={18} />
                  </div>
                  <span className="text-sm font-medium text-gray-500">Leave Type</span>
                </div>
                <p className="text-gray-900 font-medium ml-11">{mapLeaveType(leave.type)}</p>
              </div>

              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-50 rounded-lg text-orange-600">
                    <Icon name="Clock" size={18} />
                  </div>
                  <span className="text-sm font-medium text-gray-500">Duration</span>
                </div>
                <p className="text-gray-900 font-medium ml-11">{getDurationDisplay(leave)}</p>
              </div>
            </div>
            <div className="space-y-4">
              {leave.type !== LEAVE_TYPES.MULTIPLE_DAY && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-green-50 rounded-lg text-green-600">
                        <Icon name="Calendar" size={18} />
                      </div>
                      <span className="text-sm font-medium text-gray-500">Start Time</span>
                    </div>
                    <p className="text-gray-900 font-medium ml-11">
                      {leave.start_date_time
                        ? dayjs(leave.start_date_time).format('MMM DD, YYYY hh:mm A')
                        : 'N/A'}
                    </p>
                  </div>

                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                        <Icon name="Calendar" size={18} />
                      </div>
                      <span className="text-sm font-medium text-gray-500">End Time</span>
                    </div>
                    <p className="text-gray-900 font-medium ml-11">
                      {leave.end_date_time
                        ? dayjs(leave.end_date_time).format('MMM DD, YYYY hh:mm A')
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* Multiple Day Dates */}
            {isMultipleDay && (
              <div className="mb-8">
                <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Icon name="CalendarDays" size={16} className="text-gray-500" />
                  All Dates
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-gray-50 p-4 rounded-xl">
                  {[...leave.dates].sort().map((date, idx) => {
                    const isCancelled = leave?.canceled_dates?.includes(date);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 bg-white border border-${isCancelled ? 'red' : 'blue'}-200 rounded-lg text-sm transition-shadow shadow-sm`}
                      >
                        <div className="flex items-center space-x-2">
                          <div
                            className={`w-7 h-7 bg-${isCancelled ? 'red' : 'blue'}-100 rounded flex items-center justify-center`}
                          >
                            <span
                              className={`text-[10px] font-bold text-${isCancelled ? 'red' : 'blue'}-600`}
                            >
                              {new Date(date).getDate()}
                            </span>
                          </div>
                          <span className={`font-medium text-${isCancelled ? 'red' : 'blue'}-900`}>
                            {dayjs(date).format('MMM DD, YYYY')}
                          </span>
                        </div>
                        {isCancelled && (
                          <span
                            title="Cancelled"
                            className="text-red-700 font-bold bg-red-100 w-6 h-6 flex items-center justify-center rounded-full text-[10px] border border-red-200"
                          >
                            X
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Reason */}
            <div>
              <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="MessageSquare" size={16} className="text-gray-500" />
                Reason for Leave
              </h5>
              <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed border border-gray-100">
                {leave.reason || 'No reason provided.'}
              </div>
            </div>

            {leave.contact_number && (
              <div className="mt-6">
                <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Icon name="Phone" size={16} className="text-gray-500" />
                  Contact Number During Leave
                </h5>
                <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed border border-gray-100">
                  {formatContact(leave.contact_number)}
                </div>
              </div>
            )}
            {(leave.status === 'CANCELLED' || leave.status == 'REJECTED') && (
              <div className="mt-8">
                <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Icon name="MessageSquare" size={16} className="text-gray-500" />
                  Reason for {leave.status === 'CANCELLED' ? 'Cancellation' : 'Rejection'}
                </h5>
                <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed border border-gray-100">
                  {leave.cancel_reason || 'No reason provided.'}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
            {isPending ? (
              <>
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl text-sm border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => onReject(leave)}
                  className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Icon name="X" size={18} />
                  Reject Request
                </button>
                <button
                  onClick={() => onApprove(leave)}
                  className="px-2 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Icon name="Check" size={18} />
                  Approve Request
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="px-2 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Close Details
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveDetailsModal;
